import { ResponseError } from '@backstage/errors';
import { createApiRef, useRouteRef, useApi, attachComponentData, useElementFilter, createRouteRef, createExternalRouteRef, createPlugin, createApiFactory, discoveryApiRef, fetchApiRef, createRoutableExtension, createComponentExtension } from '@backstage/core-plugin-api';
import { entityRouteRef, getEntityRelations, entityRouteParams, EntityRefLinks, catalogApiRef } from '@backstage/plugin-catalog-react';
import Button from '@material-ui/core/Button';
import React from 'react';
import useAsync from 'react-use/esm/useAsync';
import { RELATION_OWNED_BY } from '@backstage/catalog-model';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Chip from '@material-ui/core/Chip';
import { ItemCardHeader, LinkButton, Content, ContentHeader, SupportButton, Progress, WarningPanel, EmptyState, ItemCardGrid, Page, Header, RoutedTabs } from '@backstage/core-components';
import pluralize from 'pluralize';
import { exploreToolsConfigRef } from '@backstage/plugin-explore-react';
import { createSearchResultListItemExtension } from '@backstage/plugin-search-react';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class ExploreClient {
  /**
   * @remarks
   *
   * The `exploreToolsConfig` is for backwards compatibility with the `exploreToolsConfigRef`•
   * and will be removed in the future.
   */
  constructor(options) {
    __publicField(this, "discoveryApi");
    __publicField(this, "fetchApi");
    // NOTE: This will be removed in the future as it is replaced by the ExploreApi.getTools method
    __publicField(this, "exploreToolsConfig");
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.exploreToolsConfig = options.exploreToolsConfig;
  }
  async getTools(request = {}) {
    var _a, _b, _c, _d, _e;
    if (this.exploreToolsConfig) {
      const tools = await this.exploreToolsConfig.getTools();
      if (tools) {
        return { tools };
      }
    }
    const { fetch } = this.fetchApi;
    const filter = (_a = request.filter) != null ? _a : {};
    const baseUrl = await this.discoveryApi.getBaseUrl("explore");
    const tags = (_c = (_b = filter == null ? void 0 : filter.tags) == null ? void 0 : _b.map((t) => `tag=${encodeURIComponent(t)}`)) != null ? _c : [];
    const lifecycles = (_e = (_d = filter == null ? void 0 : filter.lifecycle) == null ? void 0 : _d.map((l) => `lifecycle=${encodeURIComponent(l)}`)) != null ? _e : [];
    const query = [...tags, ...lifecycles].join("&");
    const response = await fetch(`${baseUrl}/tools${query ? `?${query}` : ""}`);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }
}

const exploreApiRef = createApiRef({
  id: "plugin.explore.service"
});

const EntityCard = (props) => {
  var _a, _b;
  const { entity } = props;
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);
  const url = catalogEntityRoute(entityRouteParams(entity));
  const owner = /* @__PURE__ */ React.createElement(
    EntityRefLinks,
    {
      entityRefs: ownedByRelations,
      defaultKind: "group",
      color: "inherit"
    }
  );
  return /* @__PURE__ */ React.createElement(Card, null, /* @__PURE__ */ React.createElement(CardMedia, null, /* @__PURE__ */ React.createElement(
    ItemCardHeader,
    {
      title: (_a = entity.metadata.title) != null ? _a : entity.metadata.name,
      subtitle: owner
    }
  )), /* @__PURE__ */ React.createElement(CardContent, null, ((_b = entity.metadata.tags) == null ? void 0 : _b.length) ? /* @__PURE__ */ React.createElement(Box, null, entity.metadata.tags.map((tag) => /* @__PURE__ */ React.createElement(Chip, { size: "small", label: tag, key: tag }))) : null, entity.metadata.description), /* @__PURE__ */ React.createElement(CardActions, null, /* @__PURE__ */ React.createElement(LinkButton, { to: url, color: "primary" }, "Explore")));
};

const Body = (props) => {
  const { kind } = props;
  const kindPlural = pluralize(kind);
  const catalogApi = useApi(catalogApiRef);
  const {
    value: entities,
    loading,
    error
  } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: { kind }
    });
    return response.items;
  }, [kind]);
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  }
  if (error) {
    return /* @__PURE__ */ React.createElement(WarningPanel, { severity: "error", title: `Could not load ${kindPlural}.` }, error.message);
  }
  if (!(entities == null ? void 0 : entities.length)) {
    return /* @__PURE__ */ React.createElement(
      EmptyState,
      {
        missing: "info",
        title: `No ${kindPlural} to display`,
        description: `You haven't added any ${kindPlural} yet.`,
        action: /* @__PURE__ */ React.createElement(
          Button,
          {
            variant: "contained",
            color: "primary",
            href: `https://backstage.io/docs/features/software-catalog/descriptor-format#kind-${kind}`
          },
          "Read more"
        )
      }
    );
  }
  return /* @__PURE__ */ React.createElement(ItemCardGrid, null, entities.map((entity, index) => /* @__PURE__ */ React.createElement(EntityCard, { key: index, entity })));
};
const CatalogKindExploreContent = (props) => {
  const { kind, title } = props;
  const kindLowercase = kind.toLocaleLowerCase();
  const kindCapitalized = `${kind[0].toLocaleUpperCase()}${kind.substring(1)}`;
  return /* @__PURE__ */ React.createElement(Content, { noPadding: true }, /* @__PURE__ */ React.createElement(ContentHeader, { title: title || `${pluralize(kindCapitalized)}` }, /* @__PURE__ */ React.createElement(SupportButton, null, /* @__PURE__ */ React.createElement(React.Fragment, null, "Discover the ", pluralize(kindLowercase), " in your ecosystem."))), /* @__PURE__ */ React.createElement(Body, { kind: kindLowercase }));
};

const dataKey = "plugin.explore.exploreLayoutRoute";
const Route = () => null;
attachComponentData(Route, dataKey, true);
attachComponentData(Route, "core.gatherMountPoints", true);
const ExploreLayout = (props) => {
  const { title, subtitle, children } = props;
  const routes = useElementFilter(
    children,
    (elements) => elements.selectByComponentData({
      key: dataKey,
      withStrictError: "Child of ExploreLayout must be an ExploreLayout.Route"
    }).getElements().map((child) => child.props)
  );
  return /* @__PURE__ */ React.createElement(Page, { themeId: "home" }, /* @__PURE__ */ React.createElement(
    Header,
    {
      title: title != null ? title : "Explore our ecosystem",
      subtitle: subtitle != null ? subtitle : "Discover solutions available in our ecosystem"
    }
  ), /* @__PURE__ */ React.createElement(RoutedTabs, { routes }));
};
ExploreLayout.Route = Route;

const DomainCard = (props) => {
  var _a, _b;
  const { entity } = props;
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);
  const url = catalogEntityRoute(entityRouteParams(entity));
  const owner = /* @__PURE__ */ React.createElement(
    EntityRefLinks,
    {
      entityRefs: ownedByRelations,
      defaultKind: "group",
      color: "inherit"
    }
  );
  return /* @__PURE__ */ React.createElement(Card, null, /* @__PURE__ */ React.createElement(CardMedia, null, /* @__PURE__ */ React.createElement(
    ItemCardHeader,
    {
      title: (_a = entity.metadata.title) != null ? _a : entity.metadata.name,
      subtitle: owner
    }
  )), /* @__PURE__ */ React.createElement(CardContent, null, ((_b = entity.metadata.tags) == null ? void 0 : _b.length) ? /* @__PURE__ */ React.createElement(Box, null, entity.metadata.tags.map((tag) => /* @__PURE__ */ React.createElement(Chip, { size: "small", label: tag, key: tag }))) : null, entity.metadata.description), /* @__PURE__ */ React.createElement(CardActions, null, /* @__PURE__ */ React.createElement(LinkButton, { to: url, color: "primary" }, "Explore")));
};

const exploreRouteRef = createRouteRef({
  id: "explore"
});
const catalogEntityRouteRef = createExternalRouteRef({
  id: "catalog-entity",
  params: ["namespace", "kind", "name"],
  optional: true
});

const explorePlugin = createPlugin({
  id: "explore",
  apis: [
    createApiFactory({
      api: exploreApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        exploreToolsConfig: exploreToolsConfigRef
      },
      factory: ({ discoveryApi, fetchApi, exploreToolsConfig }) => (
        // NOTE: The exploreToolsConfig is for backwards compatibility and will be removed in the future
        new ExploreClient({ discoveryApi, fetchApi, exploreToolsConfig })
      )
    }),
    /**
     * @deprecated Use ExploreApi from `@backstage/plugin-explore` instead
     *
     * Register a default for exploreToolsConfigRef, you may want to override
     * the API locally in your app.
     */
    createApiFactory({
      api: exploreToolsConfigRef,
      deps: {},
      factory: () => ({
        async getTools() {
          return void 0;
        }
      })
    })
  ],
  routes: {
    explore: exploreRouteRef
  },
  externalRoutes: {
    catalogEntity: catalogEntityRouteRef
  }
});
const ToolSearchResultListItem = explorePlugin.provide(
  createSearchResultListItemExtension({
    name: "ToolSearchResultListItem",
    component: () => import('./index-Cz8p9vYj.esm.js').then(
      (m) => m.ToolSearchResultListItem
    ),
    predicate: (result) => result.type === "tools"
  })
);

const ExplorePage = explorePlugin.provide(
  createRoutableExtension({
    name: "ExplorePage",
    component: () => import('./index-C31wDFdC.esm.js').then((m) => m.ExplorePage),
    mountPoint: exploreRouteRef
  })
);
const DomainExplorerContent = explorePlugin.provide(
  createComponentExtension({
    name: "DomainExplorerContent",
    component: {
      lazy: () => import('./index-CeA8FEjs.esm.js').then(
        (m) => m.DomainExplorerContent
      )
    }
  })
);
const GroupsExplorerContent = explorePlugin.provide(
  createComponentExtension({
    name: "GroupsExplorerContent",
    component: {
      lazy: () => import('./index-B_Dn9qT_.esm.js').then(
        (m) => m.GroupsExplorerContent
      )
    }
  })
);
const ToolExplorerContent = explorePlugin.provide(
  createComponentExtension({
    name: "ToolExplorerContent",
    component: {
      lazy: () => import('./index-D97ESU7R.esm.js').then(
        (m) => m.ToolExplorerContent
      )
    }
  })
);

export { CatalogKindExploreContent as C, DomainCard as D, EntityCard as E, GroupsExplorerContent as G, ToolSearchResultListItem as T, ExploreLayout as a, explorePlugin as b, ExploreClient as c, ExplorePage as d, exploreApiRef as e, DomainExplorerContent as f, ToolExplorerContent as g, exploreRouteRef as h, catalogEntityRouteRef as i };
//# sourceMappingURL=index-CFTA3J1_.esm.js.map
